import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { FileDown, Send, RefreshCcw, CheckCircle2, XCircle, RotateCw } from "lucide-react";
import ResearchReportsPanel from "../../components/admin/ResearchReportsPanel";

const VERSION_LABEL = { short: "Краткая", full: "Полная" };
const STATUS_LABEL = { sent: "Отправлено", pending: "Ожидает", failed: "Ошибка" };

export default function ResearchLeads() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [onlySubscribed, setOnlySubscribed] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = onlySubscribed ? "?only_subscribed=true" : "";
      const r = await api.get(`/api/admin/research/leads${params}`);
      setLeads(r.data.leads || []);
      setTotal(r.data.total || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlySubscribed]);

  const resend = async (l) => {
    if (!window.confirm(`Отправить новую ссылку на ${l.email}?`)) return;
    try {
      await api.post(`/api/admin/research/leads/${l.id}/resend`);
      load();
    } catch (e) {
      window.alert(formatApiError(e?.response?.data?.detail));
    }
  };

  const base = process.env.REACT_APP_BACKEND_URL;
  const csvSuffix = onlySubscribed ? "?only_subscribed=true" : "";
  const csvUrl = `${base}/api/admin/research/leads.csv${csvSuffix}`;
  const unisenderUrl = `${base}/api/admin/research/leads.unisender.csv`;

  return (
    <div>
      <div className="flex items-end justify-between mb-8 gap-6 flex-wrap">
        <div>
          <div className="label-mono">RESEARCH</div>
          <h1 className="text-3xl md:text-4xl font-medium mt-1">Заявки на исследование</h1>
          <p className="text-foreground/60 mt-2 text-sm">
            Лиды, собранные с landing-страницы <code className="mono text-xs">/research</code>. Всего в базе:{" "}
            <span className="tabular font-medium text-foreground">{total}</span>.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={load}
            className="btn-ghost"
            data-testid="research-leads-refresh"
            title="Обновить"
          >
            <RefreshCcw size={14} /> Обновить
          </button>
          <a
            href={csvUrl}
            className="btn-secondary"
            data-testid="research-leads-csv-btn"
          >
            <FileDown size={14} /> CSV
          </a>
          <a
            href={unisenderUrl}
            className="btn-primary"
            data-testid="research-leads-unisender-btn"
            title="Только подписчики (consent_marketing=true) в формате Unisender"
          >
            <Send size={14} /> Unisender CSV
          </a>
        </div>
      </div>

      <ResearchReportsPanel onChanged={load} />

      <label
        className="inline-flex items-center gap-2 cursor-pointer mb-6 select-none"
        data-testid="research-only-subscribed-label"
      >
        <input
          type="checkbox"
          checked={onlySubscribed}
          onChange={(e) => setOnlySubscribed(e.target.checked)}
          className="w-4 h-4 accent-black cursor-pointer"
          data-testid="research-only-subscribed-checkbox"
        />
        <span className="text-sm text-foreground/80">
          Только подписчики (согласны на рассылки)
        </span>
      </label>

      {loading ? (
        <div className="label-mono py-12 text-center">Загрузка…</div>
      ) : leads.length === 0 ? (
        <div className="border border-black/10 py-16 text-center">
          <div className="label-mono text-foreground/50">Пока нет заявок</div>
          {onlySubscribed && (
            <p className="mt-3 text-sm text-foreground/60">
              Попробуйте снять фильтр «Только подписчики» — возможно, есть неподписанные лиды.
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto border border-black/10">
          <table className="w-full text-sm" data-testid="research-leads-table">
            <thead className="bg-[hsl(var(--secondary))]">
              <tr className="text-left">
                <th className="p-3 label-mono">Дата</th>
                <th className="p-3 label-mono">Версия</th>
                <th className="p-3 label-mono">Письмо</th>
                <th className="p-3 label-mono">Скачано</th>
                <th className="p-3 label-mono">Имя, Фамилия</th>
                <th className="p-3 label-mono">E-mail</th>
                <th className="p-3 label-mono">Телефон</th>
                <th className="p-3 label-mono">Компания</th>
                <th className="p-3 label-mono">Отрасль</th>
                <th className="p-3 label-mono">Позиция</th>
                <th className="p-3 label-mono text-center">Рассылка</th>
                <th className="p-3 label-mono"></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-black/10 hover:bg-[hsl(var(--secondary))]/60">
                  <td className="p-3 tabular text-foreground/70 whitespace-nowrap">
                    {(l.created_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="p-3 whitespace-nowrap" data-testid={`lead-version-${l.id}`}>{VERSION_LABEL[l.version] || "—"}</td>
                  <td className="p-3 whitespace-nowrap" data-testid={`lead-status-${l.id}`} title={l.email_error || ""}>
                    <span className={l.status === "failed" ? "text-[hsl(var(--destructive))]" : ""}>{STATUS_LABEL[l.status] || "—"}</span>
                  </td>
                  <td className="p-3 tabular whitespace-nowrap" data-testid={`lead-downloads-${l.id}`}>
                    {l.version ? `${l.downloads_used || 0} / ${l.max_downloads || "—"}` : "—"}
                  </td>
                  <td className="p-3 whitespace-nowrap">
                    {l.first_name} {l.last_name}
                  </td>
                  <td className="p-3">
                    <a href={`mailto:${l.email}`} className="hop-link">{l.email}</a>
                  </td>
                  <td className="p-3 tabular whitespace-nowrap">{l.phone}</td>
                  <td className="p-3">{l.company_name}</td>
                  <td className="p-3 text-foreground/70">{l.industry}</td>
                  <td className="p-3 text-foreground/70">{l.position}</td>
                  <td className="p-3 text-center">
                    {l.consent_marketing ? (
                      <CheckCircle2 size={16} className="inline text-[hsl(var(--accent))]" title="Подписан" />
                    ) : (
                      <XCircle size={16} className="inline text-foreground/30" title="Не подписан" />
                    )}
                  </td>
                  <td className="p-3">
                    {l.version && (
                      <button className="btn-ghost" onClick={() => resend(l)} title="Отправить новую ссылку" data-testid={`lead-resend-${l.id}`}>
                        <RotateCw size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
